import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Lock } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";
import Stamp from "@/components/Stamp";
import Seo from "@/components/Seo";

const CHECKOUT_SEO = (
  <Seo
    title="Checkout | Anti Unhealthy Club"
    description="Demo-Checkout von Anti Unhealthy Club."
    path="/checkout"
    noindex
  />
);

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="kicker text-ink/60">{label}</span>
    <input data-testid={testid} className="mt-1.5 input-line" {...props} />
  </label>
);

const Checkout = () => {
  const { t, tr } = useLang();
  const { items, subtotal, clear } = useCart();
  const [done, setDone] = useState(false);
  const shippingFree = subtotal >= 50;
  const total = subtotal + (shippingFree ? 0 : 4.9);

  const submit = (e) => {
    e.preventDefault();
    setDone(true);
    clear();
  };

  if (done) {
    return (
      <div className="bg-cream min-h-[70vh] flex items-center justify-center px-4 py-20" data-testid="checkout-success">
        {CHECKOUT_SEO}
        <div className="card-line max-w-lg w-full p-10 text-center">
          <div className="flex justify-center mb-6"><Stamp size={110} bg="bg-lime" /></div>
          <div className="inline-flex items-center gap-2 bg-lime border border-ink rounded-full px-4 py-1.5 kicker">
            <Check className="w-4 h-4" strokeWidth={2.5} /> {t.cart.success}
          </div>
          <h1 className="display uppercase text-4xl md:text-5xl mt-5 leading-[0.9] text-brand">Fight For Yourself</h1>
          <p className="mt-4 font-medium text-ink/70">{t.cart.successSub}</p>
          <Link to="/shop" className="btn-primary h-14 px-8 mt-8 inline-flex">
            {t.cart.continue}
          </Link>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-cream min-h-[60vh] flex flex-col items-center justify-center gap-6 px-4">
        {CHECKOUT_SEO}
        <p className="display uppercase text-4xl text-ink/40 text-center">{t.cart.empty}</p>
        <Link to="/shop" className="btn-primary h-12 px-6">{t.cart.emptyCta}</Link>
      </div>
    );
  }

  return (
    <div className="bg-cream py-10 md:py-16" data-testid="page-checkout">
      {CHECKOUT_SEO}
      <div className="max-w-[1100px] mx-auto px-4 md:px-8">
        <Link to="/shop" className="inline-flex items-center gap-2 kicker hover:text-pink transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" strokeWidth={2} /> {t.cart.continue}
        </Link>
        <h1 className="display uppercase text-5xl md:text-7xl leading-[0.84] text-brand">{t.cart.checkout}</h1>

        <div className="grid lg:grid-cols-2 gap-px bg-ink border border-ink mt-8">
          <form onSubmit={submit} className="bg-cream p-6 md:p-8 space-y-4 order-2 lg:order-1" data-testid="checkout-form">
            <Field label={t.cart.name} testid="checkout-name" required placeholder="Alex Muster" />
            <Field label={t.cart.address} testid="checkout-address" required placeholder="Musterstraße 1" />
            <div className="grid grid-cols-2 gap-4">
              <Field label={t.cart.zip} testid="checkout-zip" required placeholder="10115" />
              <Field label={t.cart.city} testid="checkout-city" required placeholder="Berlin" />
            </div>
            <div className="card-line bg-sand p-4">
              <p className="kicker text-ink/60 flex items-center gap-2"><Lock className="w-3.5 h-3.5" /> {t.cart.pay}</p>
              <input disabled value="4242 4242 4242 4242" className="mt-2 input-line bg-white/60 font-mono text-ink/50" />
            </div>
            <button type="submit" data-testid="checkout-submit" className="btn-primary w-full h-16 text-lg">
              {t.cart.place} — €{total.toFixed(2)}
            </button>
            <p className="text-xs text-center text-ink/50">{t.cart.demoNote}</p>
          </form>

          <div className="bg-white p-6 md:p-8 order-1 lg:order-2">
            <h2 className="display uppercase text-2xl mb-4">{t.cart.title}</h2>
            <ul className="space-y-3">
              {items.map((i) => (
                <li key={i.id} className="flex items-center gap-3">
                  <div className="w-14 h-14 border border-ink bg-sand overflow-hidden flex-shrink-0">
                    <img src={i.image} alt={tr(i.name)} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm leading-tight">{tr(i.name)}</p>
                    <p className="text-xs text-ink/50">{t.cart.qty}: {i.qty}</p>
                  </div>
                  <span className="font-bold">€{(i.price * i.qty).toFixed(0)}</span>
                </li>
              ))}
            </ul>
            <div className="border-t border-ink mt-4 pt-4 space-y-2">
              <div className="flex justify-between font-medium"><span>{t.cart.subtotal}</span><span>€{subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between font-medium"><span>{t.cart.shipping}</span><span className={shippingFree ? "text-pink font-bold uppercase" : ""}>{shippingFree ? t.cart.free : "€4,90"}</span></div>
              <div className="flex justify-between items-end pt-2 border-t border-ink">
                <span className="display uppercase text-xl">Total</span>
                <span className="display text-3xl" data-testid="checkout-total">€{total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
